import { Injectable } from "@angular/core";
import { ReplaySubject } from "rxjs";
import { Store } from "@ngxs/store";
import { SetFilter } from "../state/apai.actions";
import { CompaniesService } from "../../../services/companies.service";
import { CompanyProducts } from "src/models/phoenix/globalTypes";

export interface CompaniesEnabled {
  userId: number;
  userRole: string;
  companies: {
    id: string;
    name: string;
    accountName: string;
    enabled: boolean;
  }[];
}

// todo: create storage service and move interface under models folder
export interface Way2VatStorage {
  access_token: string;
  payload: {
    userId: string;
    role: string;
    companiesIds: string[];
    enabledProducts?: {
      vat?: number[];
      apai?: number[];
      [key: string]: number[];
    };
  };
}

@Injectable({
  providedIn: "root",
})
export class ApaiEnabledService {
  enabledCompaniesSubject = new ReplaySubject<CompaniesEnabled>();
  enabledCompanies$ = this.enabledCompaniesSubject.asObservable();
  enabledCompanies: CompaniesEnabled;

  constructor(
    private ngxsStore: Store,
    private companiesService: CompaniesService,
  ) {}

  /**
   * Retrieves APAI enabled companies from the JWT payload stored in local storage.
   *
   * To minimize the JWT payload size, for each enabled product we store the "index" of the company,
   * where the index corresponds to the position of the company in the JWT `companiesIds` array.
   *
   * @example
   * If the JWT payload contains:
   * ```
   * {
   *   companiesIds: ["company-id-1", "company-id-2", "company-id-3"],
   *   enabledProducts: {
   *     vat: [0, 2],  // vat enabled for "company-id-1" and "company-id-3"
   *     apai: [1, 2]  // apai enabled for "company-id-2" and "company-id-3"
   *   }
   * }
   * ```
   *
   * This method will produce:
   * ```
   * companies: [
   *   { id: "company-id-1", enabled: false },
   *   { id: "company-id-2", enabled: true },
   *   { id: "company-id-3", enabled: true }
   * ]
   * ```
   */
  fetchConfig() {
    const way2vatData = this.getWay2VatData();

    if (!way2vatData) {
      console.warn("No way2vat data found in local storage");
      return;
    }

    const { payload } = way2vatData;
    const companiesIds = payload.companiesIds || [];
    const enabledProducts = payload.enabledProducts || {};
    const isAdmin = payload.role === "admin";

    if (payload.userId) {
      const entry = localStorage.getItem(
        "apai_enabled_companies" + payload.userId,
      );

      if (entry) {
        try {
          const parsed: CompaniesEnabled = JSON.parse(entry);
          this.enabledCompanies = parsed;

          this.enabledCompaniesSubject.next(parsed);
        } catch (_) {
          console.error("Error parsing stored APAI enabled companies data");
        }
      }
    }

    /*
      Extract the array of company indices that have APAI enabled.
      Example: if apaiCompanyIndices = [1, 2], it means APAI is enabled
      for companies at positions 1 and 2 in the companiesIds array.
    */
    const apaiCompanyIndices = enabledProducts.apai || [];
    const apaiCompanyIds = apaiCompanyIndices.map(
      (index) => companiesIds[index],
    );

    const companyIds = isAdmin ? [] : apaiCompanyIds; // Admins have all companies available

    const sub = this.companiesService.companiesWithEnabledProducts(
      CompanyProducts.apai,
      companyIds,
    );
    sub.subscribe({
      next: (companies) => {
        const enabledCompaniesData: CompaniesEnabled = {
          userId: Number(payload.userId),
          userRole: payload.role,
          companies,
        };

        this.enabledCompanies = enabledCompaniesData;
        this.enabledCompaniesSubject.next(enabledCompaniesData);

        localStorage.setItem(
          "apai_enabled_companies" + payload.userId,
          JSON.stringify(enabledCompaniesData),
        );

        const firstEnabledCompany = companies.find((c) => c.enabled);
        if (firstEnabledCompany) {
          this.ngxsStore.dispatch(
            new SetFilter({
              companyId: firstEnabledCompany.id,
              page: 1,
            }),
          );
        }
      },
      error: (error) => {
        console.error("Error fetching company enabled products:", error);
      },
    });
    return sub;
  }

  private getWay2VatData(): Way2VatStorage | null {
    try {
      const data = localStorage.getItem("way2vat");
      if (!data) return null;

      const parsedData = JSON.parse(data);
      const access_token = localStorage.getItem("access_token");

      const token = access_token || parsedData.access_token;
      const jwtToken = JSON.parse(atob(token.split(".")[1]));

      const expires = jwtToken.exp * 1000;
      const currTime = Date.now();
      const isExpired = currTime >= expires;

      if (isExpired) return null;
      return parsedData;
    } catch (error) {
      console.error("Error parsing way2vat data from local storage:", error);
      return null;
    }
  }
}
